import { useEffect, useState } from 'react';
import {
  FaCalendar,
  FaCheck,
  FaClock,
  FaMapMarkerAlt,
  FaStar,
  FaUsers
} from 'react-icons/fa';
import { useNavigate, useParams } from 'react-router-dom';
import { reviewsAPI, toursAPI } from '../services/api';
import './TourDetails.css';

const TourDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tour, setTour] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  // Review form state
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewForm, setReviewForm] = useState({
    name: '',
    email: '',
    rating: 5,
    comment: ''
  });
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [reviewMessage, setReviewMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    fetchTourDetails();
    fetchReviews();
  }, [id]);

  const fetchTourDetails = async () => {
    try {
      const response = await toursAPI.getById(id);
      setTour(response.data.data);
    } catch (error) {
      console.error('Error fetching tour:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchReviews = async () => {
    try {
      const response = await reviewsAPI.getByTour(id);
      setReviews(response.data.data);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    }
  };

  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    setReviewSubmitting(true);
    setReviewMessage({ type: '', text: '' });

    try {
      await reviewsAPI.create({
        tourId: id,
        customer: {
          name: reviewForm.name,
          email: reviewForm.email
        },
        rating: reviewForm.rating,
        comment: reviewForm.comment
      });

      setReviewMessage({
        type: 'success',
        text: 'Thank you! Your review has been submitted and will be visible after approval.'
      });
      setReviewForm({ name: '', email: '', rating: 5, comment: '' });
      setShowReviewForm(false);

      // Refresh reviews after a delay
      setTimeout(() => {
        fetchReviews();
      }, 1000);
    } catch (error) {
      setReviewMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to submit review. Please try again.'
      });
    } finally {
      setReviewSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    setReviewForm({
      ...reviewForm,
      [e.target.name]: e.target.value
    });
  };

  if (loading) {
    return <div className="spinner" style={{ margin: '100px auto' }}></div>;
  }

  if (!tour) {
    return (
      <div className="container section text-center">
        <h2>Tour not found</h2>
        <button onClick={() => navigate('/tours')} className="btn btn-primary mt-2">
          Back to Tours
        </button>
      </div>
    );
  }

  return (
    <div className="tour-details-page">
      {/* Hero Section */}
      <div className="tour-hero" style={{
        backgroundImage: `url(${tour.images?.[0] || 'https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=1920'})`
      }}>
        <div className="tour-hero-overlay"></div>
        <div className="container tour-hero-content">
          <span className="tour-category badge badge-primary">{tour.category}</span>
          <h1 className="tour-hero-title">{tour.title}</h1>
          <div className="tour-hero-info">
            <span><FaMapMarkerAlt /> {tour.destination}</span>
            <span><FaClock /> {tour.duration}</span>
            <span><FaUsers /> Max {tour.maxGroupSize} people</span>
            <span><FaStar /> {tour.rating || 5.0} ({tour.reviewCount || 0} reviews)</span>
          </div>
        </div>
      </div>

      <div className="container section">
        <div className="tour-details-grid">
          {/* Main Content */}
          <div className="tour-main-content">
            {/* Description */}
            <section className="detail-section">
              <h2>Tour Overview</h2>
              <p>{tour.description}</p>
            </section>

            {/* What's Included */}
            {tour.includes && tour.includes.length > 0 && (
              <section className="detail-section">
                <h2>What's Included</h2>
                <ul className="includes-list">
                  {tour.includes.map((item, index) => (
                    <li key={index}>
                      <FaCheck className="check-icon" />
                      {item}
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* Itinerary */}
            {tour.itinerary && tour.itinerary.length > 0 && (
              <section className="detail-section">
                <h2>Itinerary</h2>
                <div className="itinerary-list">
                  {tour.itinerary.map((item, index) => (
                    <div key={index} className="itinerary-item">
                      <div className="itinerary-day">Day {item.day}</div>
                      <div className="itinerary-content">
                        <h4>{item.title}</h4>
                        <p>{item.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Reviews */}
            <section className="detail-section">
              <div className="reviews-header">
                <h2>Customer Reviews ({reviews.length})</h2>
                <button
                  className="btn btn-primary"
                  onClick={() => setShowReviewForm(!showReviewForm)}
                >
                  {showReviewForm ? 'Cancel' : 'Write a Review'}
                </button>
              </div>

              {/* Review Submission Message */}
              {reviewMessage.text && (
                <div className={`alert alert-${reviewMessage.type}`}>
                  {reviewMessage.text}
                </div>
              )}

              {/* Review Form */}
              {showReviewForm && (
                <div className="review-form-card">
                  <h3>Share Your Experience</h3>
                  <form onSubmit={handleReviewSubmit}>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Your Name *</label>
                        <input
                          type="text"
                          name="name"
                          value={reviewForm.name}
                          onChange={handleInputChange}
                          required
                          placeholder="Enter your name"
                        />
                      </div>
                      <div className="form-group">
                        <label>Your Email *</label>
                        <input
                          type="email"
                          name="email"
                          value={reviewForm.email}
                          onChange={handleInputChange}
                          required
                          placeholder="Enter your email"
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <label>Rating *</label>
                      <div className="rating-input">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <FaStar
                            key={star}
                            className={`star-icon ${reviewForm.rating >= star ? 'active' : ''}`}
                            onClick={() => setReviewForm({ ...reviewForm, rating: star })}
                            style={{ cursor: 'pointer', fontSize: '24px' }}
                          />
                        ))}
                        <span style={{ marginLeft: '10px' }}>
                          ({reviewForm.rating} {reviewForm.rating === 1 ? 'star' : 'stars'})
                        </span>
                      </div>
                    </div>

                    <div className="form-group">
                      <label>Your Review *</label>
                      <textarea
                        name="comment"
                        value={reviewForm.comment}
                        onChange={handleInputChange}
                        required
                        rows="5"
                        maxLength="500"
                        placeholder="Tell us about your experience with this tour..."
                      />
                      <small>{reviewForm.comment.length}/500 characters</small>
                    </div>

                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={reviewSubmitting}
                    >
                      {reviewSubmitting ? 'Submitting...' : 'Submit Review'}
                    </button>
                  </form>
                </div>
              )}

              {/* Reviews List */}
              {reviews.length > 0 ? (
                <div className="reviews-list">
                  {reviews.map((review) => (
                    <div key={review._id} className="review-card">
                      <div className="review-header">
                        <div>
                          <h4>{review.customer.name}</h4>
                          <div className="review-rating">
                            {[...Array(5)].map((_, i) => (
                              <FaStar
                                key={i}
                                className={`star-icon ${i < review.rating ? 'active' : ''}`}
                              />
                            ))}
                          </div>
                        </div>
                        <span className="review-date">
                          {new Date(review.createdAt).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </span>
                      </div>
                      <p className="review-comment">{review.comment}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="no-reviews">
                  <p>No reviews yet. Be the first to review this tour!</p>
                </div>
              )}
            </section>
          </div>

          {/* Booking Sidebar */}
          <div className="tour-sidebar">
            <div className="booking-card card">
              <div className="price-section">
                <h2 className="price-value">₹{tour.price}</h2>
              </div>

              <div className="booking-info">
                <div className="info-row">
                  <FaCalendar />
                  <span>Available dates: Contact us</span>
                </div>
                <div className="info-row">
                  <FaClock />
                  <span>Duration: {tour.duration}</span>
                </div>
              </div>

              <button
                onClick={() => navigate(`/booking/${tour._id}`)}
                className="btn btn-primary"
                style={{ width: '100%', padding: '15px' }}
                disabled={!tour.availability}
              >
                {tour.availability ? 'Book Now' : 'Not Available'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TourDetails;
